/**
 * External Imports
 */
import { FC, useEffect, useLayoutEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

/**
 * Internal Imports
 */
import EmployeeProfileHeader from '../../components/profileHeaders/employeeProfileHeader';
import Round from '../../components/round/Round';
import Loading from '../../components/shared/Loading';
import HistorySideNav from '../../components/sideNav/historySideNav';
import HistorySideDrawer from '../../components/sideNav/historySideDrawer';

import { RootState } from '../../redux/store';
import {
  getEmployeeRoundHistory,
  getTasksByEmployeeId,
  getQuestionBank,
  clearEmployeeRoundHistory
} from '../../redux/actions';
import {
  useGetEmployeeNotesQuery,
  useGetEmployeeToRoundQuery
} from '../../redux/apis/Employees.api';

/**
 * Global Type Definition Imports
 */
import { Feedback, Issue, RoundHistory } from '../../config/interfaces';

/**
 * Style Imports
 */
import { ProfileContainer } from './styles/Support.styles';

interface SupportProfileProps {
  employeeRoundHistory?: RoundHistory[];
  issues?: Issue[];
  feedback?: Feedback[];
}

const SupportProfile: FC<SupportProfileProps> = ({
  employeeRoundHistory,
  issues,
  feedback
}) => {
  const dispatch = useDispatch();

  const [selectedTab, setSelectedTab] = useState<string>('');

  const { state } = useLocation();
  const navigate = useNavigate();
  const { supportType = '' } = useParams();
  const { employeeId = '' } =
    (state as {
      employeeId: string;
    }) || {};

  const {
    data: employeeToRoundData,
    // isError: isGetEmployeeToRoundError,
    isFetching: isGetEmployeeToRoundFetching
    // isLoading: isGetEmployeeToRoundLoading
  } = useGetEmployeeToRoundQuery(employeeId, {
    refetchOnMountOrArgChange: true,
    skip: !employeeId
  });
  const {
    data: employeeNotesData,
    // isError: isGetEmployeeNotesError,
    // isFetching: isGetEmployeeNotesFetching
    isLoading: isGetEmployeeNotesLoading
  } = useGetEmployeeNotesQuery(employeeId, {
    pollingInterval: 60000,
    refetchOnMountOrArgChange: true,
    skip: !employeeToRoundData?.employeeId
  });

  useLayoutEffect(() => {
    if (employeeToRoundData) {
      dispatch(
        getEmployeeRoundHistory({
          employeeId: employeeToRoundData.hcaid,
          roundingTypes: ['csrn', 'cned']
        })
      );
      dispatch(
        getTasksByEmployeeId({
          employeeId: employeeToRoundData?.hcaid,
          roundingTypes: ['employee', 'cned', 'csrn']
        })
      );
    } else if (!employeeId) {
      // TODO: Hack to return to patient census when refreshing or navigating straight to profile
      navigate(window.localStorage.getItem('orbitLastPath') ?? '/');
    }
  }, [employeeToRoundData]);

  useEffect(() => {
    return () => {
      dispatch(clearEmployeeRoundHistory());
    };
  }, []);

  useEffect(() => {
    if (employeeToRoundData) {
      dispatch(getQuestionBank());
    }
  }, [employeeToRoundData]);

  return (
    <ProfileContainer id="Support-Profile">
      {isGetEmployeeToRoundFetching || isGetEmployeeNotesLoading ? (
        <Loading centered label="subject data..." />
      ) : (
        <>
          <section>
            <EmployeeProfileHeader selectedEmployee={employeeToRoundData} />
          </section>
          <section className="d-flex" style={{ height: 'calc(100vh - 163px)' }}>
            <HistorySideNav
              selectedEmployee={{
                ...employeeToRoundData,
                notes: employeeNotesData || []
              }}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              type="support"
              supportType={supportType}
            />
            <HistorySideDrawer
              selectedTab={selectedTab}
              selectedPerson={{
                ...employeeToRoundData,
                notes: employeeNotesData || []
              }}
              roundHistory={employeeRoundHistory}
              tasks={[...(issues || []), ...(feedback || [])]}
              type="support"
              // TODO: This needs to be changed with phase II of csc rename
              supportType={supportType === 'csc' ? 'csrn' : supportType}
              setSelectedTab={setSelectedTab}
            />
            <Round
              type={supportType}
              selectedEmployee={{
                ...employeeToRoundData,
                notes: employeeNotesData || []
              }}
            />
          </section>
        </>
      )}
    </ProfileContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    employeeRoundHistory: state.ReportReducer.employeeRoundHistory,
    issues: state.TaskReducer.employeeIssues,
    feedback: state.TaskReducer.employeeFeedback
  };
};

export default connect(mapReduxStateToProps)(SupportProfile);
